// Pure state machine for the Security Access handshake: IDLE -> SEED_REQUESTED ->
// (GRANTED | back to SEED_REQUESTED on a wrong key) -> LOCKED after too many wrong keys,
// with LOCKED auto-expiring back to IDLE. No React/timers/randomness live here — the seed
// value and "now" timestamp are always passed in, so this module stays trivially testable
// and mirrors the frontend/src/lib/uds/engine.js convention used by the other UDS tool.
import { toHexBytes, toUint32 } from './hexUtils';
import { computeKey } from './algorithms';
import { DEFAULT_MAX_ATTEMPTS, DEFAULT_LOCKOUT_MS } from './constants';

export const PHASE = {
  IDLE: 'idle',
  SEED_REQUESTED: 'seedRequested',
  GRANTED: 'granted',
  LOCKED: 'locked',
};

export const STATUS = {
  NONE: 'none',
  GRANTED: 'granted',
  INVALID_KEY: 'invalidKey',
  INVALID_SEQUENCE: 'invalidSequence',
  LOCKED_OUT: 'lockedOut',
};

export const createInitialState = ({ maxAttempts = DEFAULT_MAX_ATTEMPTS, lockoutDurationMs = DEFAULT_LOCKOUT_MS } = {}) => ({
  phase: PHASE.IDLE,
  seed: null,
  expectedKey: null,
  attempts: 0,
  maxAttempts,
  lockoutUntil: null,
  lockoutDurationMs,
  status: STATUS.NONE,
  log: [],
});

let logCounter = 0;
const logEntry = (direction, bytes, note) => ({
  id: `log-${++logCounter}`,
  direction, // 'tester' | 'ecu'
  bytesHex: bytes.map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(' '),
  note,
});

const nrcResponse = (sid, code) => [0x7f, sid, code];

const SID = 0x27;

// state -> { ...state, log: [...] } with a Request Seed exchange appended. `forceZeroSeed`
// lets the UI deterministically demonstrate the "already unlocked" (seed 0x00000000) case
// instead of relying on a lucky random draw.
export const requestSeed = (state, { level, algorithmId, param, algoOpts, seedValue }) => {
  const seedNum = toUint32(seedValue);
  const requestLog = logEntry('tester', [SID, level.requestSubFn]);

  if (seedNum === 0) {
    const responseLog = logEntry('ecu', [0x67, level.requestSubFn, 0, 0, 0, 0], 'Already unlocked');
    return {
      ...state,
      phase: PHASE.GRANTED,
      seed: 0,
      expectedKey: null,
      status: STATUS.GRANTED,
      log: [...state.log, requestLog, responseLog],
    };
  }

  const expectedKey = computeKey(algorithmId, seedNum, param, algoOpts);
  const seedBytes = toHexBytes(seedNum).map((h) => parseInt(h, 16));
  const responseLog = logEntry('ecu', [0x67, level.requestSubFn, ...seedBytes]);

  return {
    ...state,
    phase: PHASE.SEED_REQUESTED,
    seed: seedNum,
    expectedKey,
    status: STATUS.NONE,
    log: [...state.log, requestLog, responseLog],
  };
};

// state -> { ...state, log: [...] } after evaluating a sent key against the currently
// pending expected key.
export const sendKey = (state, { level, enteredKey }) => {
  const keyBytes = toHexBytes(enteredKey).map((h) => parseInt(h, 16));
  const requestLog = logEntry('tester', [SID, level.sendKeySubFn, ...keyBytes]);

  if (state.phase === PHASE.LOCKED) {
    const responseLog = logEntry('ecu', nrcResponse(SID, 0x37));
    return { ...state, status: STATUS.LOCKED_OUT, log: [...state.log, requestLog, responseLog] };
  }

  if (state.phase !== PHASE.SEED_REQUESTED) {
    const responseLog = logEntry('ecu', nrcResponse(SID, 0x24));
    return { ...state, status: STATUS.INVALID_SEQUENCE, log: [...state.log, requestLog, responseLog] };
  }

  if (toUint32(enteredKey) === state.expectedKey) {
    const responseLog = logEntry('ecu', [0x67, level.sendKeySubFn]);
    return {
      ...state,
      phase: PHASE.GRANTED,
      status: STATUS.GRANTED,
      attempts: 0,
      log: [...state.log, requestLog, responseLog],
    };
  }

  const attempts = state.attempts + 1;
  if (attempts >= state.maxAttempts) {
    const responseLog = logEntry('ecu', nrcResponse(SID, 0x36), 'Locked out');
    return {
      ...state,
      phase: PHASE.LOCKED,
      attempts,
      lockoutUntil: Date.now() + state.lockoutDurationMs,
      status: STATUS.LOCKED_OUT,
      log: [...state.log, requestLog, responseLog],
    };
  }

  const responseLog = logEntry('ecu', nrcResponse(SID, 0x35));
  return { ...state, phase: PHASE.SEED_REQUESTED, attempts, status: STATUS.INVALID_KEY, log: [...state.log, requestLog, responseLog] };
};

// Called on a tick (e.g. every second from a UI timer) — pure function of state + "now",
// so it stays testable without faking global timers.
export const tickLockout = (state, now) => {
  if (state.phase !== PHASE.LOCKED || state.lockoutUntil === null || now < state.lockoutUntil) return state;
  return {
    ...state,
    phase: PHASE.IDLE,
    seed: null,
    expectedKey: null,
    attempts: 0,
    lockoutUntil: null,
    status: STATUS.NONE,
  };
};

export const resetSession = (state) => createInitialState({ maxAttempts: state.maxAttempts, lockoutDurationMs: state.lockoutDurationMs });
